import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Users, GraduationCap, ClipboardCheck, Wallet, UserPlus, Plus, Star, Search, X } from "lucide-react";
import AvatarUpload from "@/components/app/PhotoUpload";
import { PageHeader } from "@/components/app/PageHeader";

function Stat({ label, value, icon: Icon, tone = "orange", delay = 0 }) {
  const tones = {
    orange: "bg-gradient-to-br from-orange-600 to-orange-400 shadow-orange-500/30",
    blue: "bg-gradient-to-br from-blue-900 to-blue-600 shadow-blue-800/30",
    gold: "bg-gradient-to-br from-yellow-500 to-orange-500 shadow-yellow-500/30",
  };
  return (
    <Card className="pmm-pop group rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: `${delay}s` }}>
      <CardContent className="flex items-center gap-4 p-6">
        <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 ${tones[tone] || tones.orange}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">
            {value ?? "—"}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminHome() {
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="pmm-cinematic relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 p-8 shadow-2xl shadow-blue-900/25 sm:p-10">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-yellow-400/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-yellow-400">
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
            {t("admin_dashboard_title")}
          </div>
          <h1 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
            {t("dashboard")}
          </h1>
          <p className="mt-2 max-w-lg font-semibold text-blue-200">
            {lang === "ms" ? "Pantau keseluruhan operasi Kelas Program Membaca PMM di satu tempat." : "Monitor the entire PMM Reading Class operation in one place."}
          </p>
          <div className="mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-yellow-400 to-transparent" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label={t("students")} value={summary.students} icon={GraduationCap} tone="orange" delay={0.05} />
        <Stat label={t("parent") + "s"} value={summary.parents} icon={Users} tone="blue" delay={0.12} />
        <Stat label={t("teacher") + "s"} value={summary.teachers} icon={Users} tone="gold" delay={0.19} />
        <Stat label={t("staffRole")} value={summary.staff} icon={Users} tone="blue" delay={0.26} />
        <Stat label={t("attendance") + " " + t("students")} value={summary.students_today} icon={ClipboardCheck} tone="orange" delay={0.33} />
        <Stat label={t("staff") + " " + t("attendance")} value={summary.staff_checkins_today} icon={ClipboardCheck} tone="blue" delay={0.4} />
        <Stat label={t("payments")} value={summary.payments_today} icon={Wallet} tone="gold" delay={0.47} />
      </div>
    </div>
  );
}

export function UsersPage() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "teacher" });

  const load = () => api.get("/users").then((r) => setUsers(r.data.users));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "teacher" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const uploadPhoto = async (userId, dataUrl) => {
    try {
      await api.put(`/users/${userId}/photo`, { photo_data_url: dataUrl });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const roleBadge = {
    admin: "bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-sm shadow-yellow-500/30",
    staff: "bg-blue-800/10 text-blue-800",
    teacher: "bg-orange-500/10 text-orange-600",
    parent: "bg-emerald-600/10 text-emerald-700",
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("users")}
        subtitle="Manage staff, teachers and parents."
        action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105">
              <UserPlus className="h-4 w-4" />
              {t("createUser")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("createUser")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("fullName")}</Label><Input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" /></div>
              <div><Label>{t("email")}</Label><Input data-testid="cu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" /></div>
              <div><Label>{t("phone")}</Label><Input data-testid="cu-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" /></div>
              <div><Label>{t("password")}</Label><Input data-testid="cu-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" /></div>
              <div>
                <Label>{t("role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="cu-role" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">{t("staffRole")}</SelectItem>
                    <SelectItem value="teacher">{t("teacher")}</SelectItem>
                    <SelectItem value="parent">{t("parent")}</SelectItem>
                    <SelectItem value="admin">{t("admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} data-testid="cu-save" className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white">{t("submit")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        }
      />
      <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.15s" }}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-950 hover:bg-blue-950">
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("fullName")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("email")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("role")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("phone")}</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`} className="transition-colors hover:bg-orange-50">
                  <TableCell className="font-semibold text-blue-950">
                    <div className="flex items-center gap-3">
                      <AvatarUpload
                        name={u.name}
                        photo={u.photo_data_url}
                        onUpload={(dataUrl) => uploadPhoto(u.id, dataUrl)}
                        size="sm"
                        testId={`user-avatar-${u.email}`}
                      />
                      {u.name}
                    </div>
                  </TableCell>
                  <TableCell className="num text-sm">{u.email}</TableCell>
                  <TableCell><span className={`rounded-full px-3 py-1 text-xs font-bold ${roleBadge[u.role] || "bg-muted"}`}>{u.role}</span></TableCell>
                  <TableCell className="num">{u.phone || "—"}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" className="rounded-full text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => del(u.id)}>×</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function StudentsAdminPage() {
  const { t, lang } = useI18n();
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [form, setForm] = useState({ full_name: "", level: "", school: "", parent_email: "", date_of_birth: "", notes: "" });

  const load = () => api.get("/students").then((r) => setStudents(r.data.students));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/students", form);
      toast.success("Student saved");
      setOpen(false);
      setForm({ full_name: "", level: "", school: "", parent_email: "", date_of_birth: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this student?")) return;
    try { await api.delete(`/students/${id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadStudentPhoto = async (studentId, dataUrl) => {
    try {
      await api.put(`/students/${studentId}/photo`, { photo_data_url: dataUrl });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const levels = [...new Set(students.map((s) => s.level).filter(Boolean))].sort();
  const filtered = students.filter((s) => {
    if (levelFilter !== "all" && s.level !== levelFilter) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return [s.full_name, s.school, s.parent_email].some((v) => (v || "").toLowerCase().includes(needle));
  });

  return (
    <div className="space-y-6" data-testid="admin-students">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("students")}
        subtitle={lang === "ms" ? "Semua murid berdaftar Program Membaca PMM." : "All registered PMM Reading Program students."}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-student-btn" className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105">
                <Plus className="h-4 w-4" />
                {t("registerChild")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("registerChild")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("fullName")}</Label><Input data-testid="cs-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-11" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("level")}</Label><Input data-testid="cs-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="h-11" placeholder="Tahap 1 (Asas)" /></div>
                  <div><Label>{t("dateOfBirth")}</Label><Input data-testid="cs-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="h-11" /></div>
                </div>
                <div><Label>{t("school")}</Label><Input data-testid="cs-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="h-11" /></div>
                <div><Label>Parent {t("email")}</Label><Input data-testid="cs-parent-email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="h-11" placeholder="parent@example.com" /></div>
                <Button onClick={save} data-testid="cs-save" className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white">{t("saveChild")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="pmm-pop flex flex-col gap-3 sm:flex-row sm:items-center" style={{ animationDelay: "0.1s" }} data-testid="students-filter-bar">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="students-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "ms" ? "Cari nama, sekolah atau emel..." : "Search name, school or email..."}
            className="h-11 rounded-full border-border/70 bg-background pl-10 pr-10"
          />
          {q && (
            <button
              type="button"
              data-testid="students-search-clear"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger data-testid="students-level-filter" className="h-11 w-full rounded-full sm:w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "ms" ? "Semua tahap" : "All levels"}</SelectItem>
            {levels.map((lv) => <SelectItem key={lv} value={lv}>{lv}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="num shrink-0 text-xs font-bold uppercase tracking-widest text-muted-foreground" data-testid="students-count">
          {filtered.length} / {students.length} {t("students")}
        </span>
      </div>
      <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.15s" }}>
        <CardContent className="p-0">
          <div className="divide-y divide-border/60 md:hidden" data-testid="students-mobile-list">
            {filtered.map((s) => (
              <div key={s.id} data-testid={`stu-card-${s.id}`} className="flex items-start justify-between gap-3 p-4">
                <AvatarUpload
                  name={s.full_name}
                  photo={s.photo_data_url}
                  onUpload={(dataUrl) => uploadStudentPhoto(s.id, dataUrl)}
                  testId={`stu-photo-m-${s.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-extrabold text-blue-950">{s.full_name}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-blue-800/10 px-2.5 py-0.5 text-[11px] font-bold text-blue-800">{s.level}</span>
                    {s.school && <span className="text-xs font-semibold text-muted-foreground">{s.school}</span>}
                  </div>
                  {s.parent_email && <div className="num mt-1 truncate text-xs text-muted-foreground">{s.parent_email}</div>}
                </div>
                <Button variant="ghost" size="sm" className="shrink-0 rounded-full text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => del(s.id)}>×</Button>
              </div>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{q || levelFilter !== "all" ? (lang === "ms" ? "Tiada murid sepadan" : "No matching students") : t("noRecordsYet")}</div>}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-950 hover:bg-blue-950">
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("fullName")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("level")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("school")}</TableHead>
                <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Parent</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`stu-row-${s.id}`} className="transition-colors hover:bg-orange-50">
                  <TableCell className="font-semibold text-blue-950">
                    <div className="flex items-center gap-3">
                      <AvatarUpload
                        name={s.full_name}
                        photo={s.photo_data_url}
                        onUpload={(dataUrl) => uploadStudentPhoto(s.id, dataUrl)}
                        size="sm"
                        testId={`stu-photo-${s.id}`}
                      />
                      <span>{s.full_name}</span>
                    </div>
                  </TableCell>
                  <TableCell><span className="rounded-full bg-blue-800/10 px-3 py-1 text-xs font-bold text-blue-800">{s.level}</span></TableCell>
                  <TableCell>{s.school || "—"}</TableCell>
                  <TableCell className="num text-sm">{s.parent_email || "—"}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" className="rounded-full text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => del(s.id)}>×</Button></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{q || levelFilter !== "all" ? (lang === "ms" ? "Tiada murid sepadan" : "No matching students") : t("noRecordsYet")}</TableCell></TableRow>}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AttendanceAdminPage() {
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  useEffect(() => {
    api.get("/attendance/staff").then((r) => setStaff(r.data.records)).catch(() => {});
    api.get("/attendance/student").then((r) => setStudents(r.data.records)).catch(() => {});
  }, []);
  return (
    <div className="space-y-8" data-testid="admin-attendance">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("attendance")}
        subtitle={`${t("staff")}, ${t("teacher")} & ${t("students")}`}
      />
      <section className="pmm-pop" style={{ animationDelay: "0.1s" }}>
        <div className="mb-3 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-blue-900 to-blue-600 text-white shadow-md shadow-blue-800/30"><ClipboardCheck className="h-4 w-4" /></span>
          <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">{t("staff")} & {t("teacher")}</h2>
        </div>
        <Card className="overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg"><CardContent className="p-0">
          <div className="divide-y divide-border/60 md:hidden" data-testid="staff-attendance-mobile-list">
            {staff.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 font-display font-extrabold text-blue-950">{r.user_name}</div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${r.action === "check_in" ? "bg-emerald-600/10 text-emerald-700" : "bg-orange-500/10 text-orange-600"}`}>{r.action}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-800/10 px-2.5 py-0.5 text-[11px] font-bold text-blue-800">{r.user_role}</span>
                  <span className="num text-xs text-muted-foreground">{r.method}</span>
                </div>
                <div className="num mt-1 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
            {staff.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noRecordsYet")}</div>}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
              <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Name</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("role")}</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Method</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Action</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{staff.map((r) => (
              <TableRow key={r.id} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{r.user_name}</TableCell>
                <TableCell><span className="rounded-full bg-blue-800/10 px-3 py-1 text-xs font-bold text-blue-800">{r.user_role}</span></TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className={`rounded-full px-3 py-1 text-xs font-bold ${r.action === "check_in" ? "bg-emerald-600/10 text-emerald-700" : "bg-orange-500/10 text-orange-600"}`}>{r.action}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{staff.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
          </div>
        </CardContent></Card>
      </section>
      <section className="pmm-pop" style={{ animationDelay: "0.2s" }}>
        <div className="mb-3 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-md shadow-orange-500/30"><GraduationCap className="h-4 w-4" /></span>
          <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">{t("students")}</h2>
        </div>
        <Card className="overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg"><CardContent className="p-0">
          <div className="divide-y divide-border/60 md:hidden" data-testid="student-attendance-mobile-list">
            {students.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 font-display font-extrabold text-blue-950">{r.student_name}</div>
                  <span className="shrink-0 rounded-full bg-emerald-600/10 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700">{r.status}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-800/10 px-2.5 py-0.5 text-[11px] font-bold text-blue-800">{r.level}</span>
                  <span className="num text-xs text-muted-foreground">{r.method}</span>
                </div>
                <div className="num mt-1 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
            {students.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noRecordsYet")}</div>}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
              <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Student</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("level")}</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Method</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Status</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{students.map((r) => (
              <TableRow key={r.id} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{r.student_name}</TableCell>
                <TableCell><span className="rounded-full bg-blue-800/10 px-3 py-1 text-xs font-bold text-blue-800">{r.level}</span></TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className="rounded-full bg-emerald-600/10 px-3 py-1 text-xs font-bold text-emerald-700">{r.status}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{students.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
          </div>
        </CardContent></Card>
      </section>
    </div>
  );
}

export function PaymentsAdminPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/payments/history").then((r) => setRows(r.data.transactions)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-payments">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("payments")}
      />
      <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.15s" }}><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">User</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Package</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Amount</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Status</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">When</TableHead>
          </TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={r.session_id} className="transition-colors hover:bg-orange-50">
              <TableCell className="num text-sm">{r.user_id}</TableCell>
              <TableCell className="font-semibold text-blue-950">{r.package_name}</TableCell>
              <TableCell className="num font-extrabold text-blue-950">{(r.currency || "").toUpperCase()} {Number(r.amount).toFixed(2)}</TableCell>
              <TableCell>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${r.payment_status === "paid" ? "bg-emerald-600/10 text-emerald-700" : "bg-orange-500/10 text-orange-600"}`}>
                  {r.payment_status}
                </span>
              </TableCell>
              <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}{rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

